import { FilterData } from '@/src/types/flight'
import { useMemo } from 'react'
import useAirlineFilterManager from '../airlineFilterManager'
import useFlightTimingFilterManager from '../flightTimingFilterManager'
import useIncludeAirportsManager from '../includeAirportsManager'
import useLayoverAirportFilterManager from '../layoverAirportFilterManager'
import useLayoverDurationFilterManager from '../layoverDurationFilterManager'
import usePriceRangeFilterManager from '../priceRangeFilterManager'
import useStopFilterManager from '../stopFilterManager'
import useTripDurationFilterManager from '../tripDurationFilterManager'
import useSearchResults from '../useSearchResults'
import useUpdateSearchResultCache from '../updateSearchResultCache'
import { useCallback } from 'react'

const resetAllMulticityJourneyFilters = (filterData: FilterData, journeyCount: number) => {
    for (let index = 0; index < journeyCount; index++) {
        if (filterData.NewStops?.[index]) {
            filterData.NewStops[index].selectedValues = []
        }
        if (filterData.airlineFilter?.[index]) {
            filterData.airlineFilter[index].selectedValues = []
        }
        if (filterData.departureTimeFrom?.[index]) {
            filterData.departureTimeFrom[index].selectedValues = []
        }
        if (filterData.arrivalTimeFrom?.[index]) {
            filterData.arrivalTimeFrom[index].selectedValues = []
        }

        const tripDuration = filterData.tripDuration?.[index]
        if (tripDuration) {
            tripDuration.current = { min: tripDuration.min, max: tripDuration.max }
        }

        const layoverDuration = filterData.layoverDuration?.[index]
        if (layoverDuration) {
            layoverDuration.current = { min: layoverDuration.min, max: layoverDuration.max }
        }

        if (filterData.layover_Airport?.[index]) {
            filterData.layover_Airport[index].selectedOptions = []
        }

        filterData.nbAirportFilter?.[index]?.forEach(group => {
            group.selectedValues = []
        })
    }

    const priceFilter = filterData.journeyPrice?.[0]
    if (priceFilter) {
        priceFilter.current = { min: priceFilter.min, max: priceFilter.max }
    }
}

const countMulticityFiltersApplied = (filterData: FilterData | undefined, journeyCount: number) => {
    if (!filterData) return 0

    let count = 0

    const isDurationFilterApplied = (min?: number, max?: number, currentMin?: number, currentMax?: number) =>
        currentMin !== min || currentMax !== max

    for (let index = 0; index < journeyCount; index++) {
        if (filterData.NewStops?.[index]?.selectedValues?.length) count++
        if (filterData.airlineFilter?.[index]?.selectedValues?.length) count++
        if (filterData.departureTimeFrom?.[index]?.selectedValues?.length) count++
        if (filterData.arrivalTimeFrom?.[index]?.selectedValues?.length) count++

        const tripDuration = filterData.tripDuration?.[index]
        if (tripDuration && isDurationFilterApplied(tripDuration.min, tripDuration.max, tripDuration.current?.min, tripDuration.current?.max)) count++

        const layoverDuration = filterData.layoverDuration?.[index]
        if (layoverDuration && isDurationFilterApplied(layoverDuration.min, layoverDuration.max, layoverDuration.current?.min, layoverDuration.current?.max)) count++

        if (filterData.layover_Airport?.[index]?.selectedOptions?.length) count++

        const nbGroups = filterData.nbAirportFilter?.[index] ?? []
        if (nbGroups.some(group => group.selectedValues?.length > 0)) count++
    }

    const priceFilter = filterData.journeyPrice?.[0]
    if (priceFilter && isDurationFilterApplied(priceFilter.min, priceFilter.max, priceFilter.current?.min, priceFilter.current?.max)) count++

    return count
}

/** Header-level aggregate for multicity — any journey filter applied across all sections. */
export const useMulticityFiltersAggregate = () => {
    const { filterData, journeyCount, isMulticity } = useSearchResults()
    const updateCache = useUpdateSearchResultCache()

    const { shouldShowResetForAllJourneys: stopsShow, stopResetAllJourneys, title: stopsLabel } = useStopFilterManager()
    const { shouldShowResetForAllJourneys: airlineShow, handleResetAllJourneys: airlineReset, title: airlineLabel } = useAirlineFilterManager()
    const { shouldShowResetForAllJourneys: depShow, handleResetAllJourneys: depReset, title: depLabel } = useFlightTimingFilterManager(0, 'departure')
    const { shouldShowResetForAllJourneys: arrShow, handleResetAllJourneys: arrReset, title: arrLabel } = useFlightTimingFilterManager(0, 'arrival')
    const { isPriceRangeFilterApplied: priceShow, handleReset: priceReset, title: priceLabel } = usePriceRangeFilterManager()
    const { shouldShowResetForAllJourneys: includeAirportsShow, handleResetAllJourneys: includeAirportsReset, title: includeAirportsLabel } = useIncludeAirportsManager()
    const { shouldShowResetForAllJourneys: layoverAirportShow, handleResetAllJourneys: layoverAirportReset, title: layoverAirportLabel } = useLayoverAirportFilterManager()
    const { shouldShowResetForAllJourneys: tripDurationShow, handleResetAllJourneys: tripDurationReset, title: tripDurationLabel } = useTripDurationFilterManager()
    const { shouldShowResetForAllJourneys: layoverDurationShow, handleResetAllJourneys: layoverDurationReset, title: layoverDurationLabel } = useLayoverDurationFilterManager()

    const numberOfFiltersApplied = useMemo(
        () => (isMulticity ? countMulticityFiltersApplied(filterData, journeyCount) : 0),
        [filterData, journeyCount, isMulticity]
    )

    const isFilterApplied = numberOfFiltersApplied > 0

    const appliedFilterList = useMemo(() => {
        if (!isMulticity) return []
        return [
            { show: stopsShow, label: stopsLabel, onDelete: stopResetAllJourneys },
            { show: airlineShow, label: airlineLabel, onDelete: airlineReset },
            { show: depShow, label: depLabel, onDelete: depReset },
            { show: arrShow, label: arrLabel, onDelete: arrReset },
            { show: priceShow, label: priceLabel, onDelete: priceReset },
            { show: includeAirportsShow, label: includeAirportsLabel, onDelete: includeAirportsReset },
            { show: layoverAirportShow, label: layoverAirportLabel, onDelete: layoverAirportReset },
            { show: tripDurationShow, label: tripDurationLabel, onDelete: tripDurationReset },
            { show: layoverDurationShow, label: layoverDurationLabel, onDelete: layoverDurationReset },
        ]
    }, [
        isMulticity,
        stopsShow, stopsLabel, stopResetAllJourneys,
        airlineShow, airlineLabel, airlineReset,
        depShow, depLabel, depReset,
        arrShow, arrLabel, arrReset,
        priceShow, priceLabel, priceReset,
        includeAirportsShow, includeAirportsLabel, includeAirportsReset,
        layoverAirportShow, layoverAirportLabel, layoverAirportReset,
        tripDurationShow, tripDurationLabel, tripDurationReset,
        layoverDurationShow, layoverDurationLabel, layoverDurationReset,
    ])

    const clearAllMulticityFilters = useCallback(() => {
        updateCache(currentData => {
            if (currentData?.filterData) {
                resetAllMulticityJourneyFilters(currentData.filterData, journeyCount)
            }
            return currentData as NonNullable<typeof currentData>
        })
    }, [journeyCount, updateCache])

    return { isFilterApplied, numberOfFiltersApplied, clearAllMulticityFilters, appliedFilterList }
}
